import React from 'react';

import ErrorBoundary from 'ErrorBoundary';
import getLocal from 'services/getLocal';
import FullscreenLoaderSVG from 'components/atoms/FullscreenLoaderSVG';
import MainContextProviderProduction from './MainContextProviderProduction';

type Meta = {
  auth?: boolean;
  notAuth?: boolean;
  admin?: boolean;
  notAdmin?: boolean;
  licenced?: boolean;
  payments?: boolean;
  withOpenedSignIn?: boolean;
  isAnalytics?: boolean;
}

const ANALYTICS_AVAILABLE_PAGES = [
  'privacy',
  'terms',
  'faq',
  'how-to',
  'glossary',
  'email_confirmation'
];

const INTEGRATIONS_DOMAIN = process.env.REACT_APP_INTEGRATIONS_DOMAIN ?? 'https://analytics.tigim.co';

const shouldMoveToIntegrations = (): boolean => {
  const origin = window.location.origin;
  if (origin.includes(INTEGRATIONS_DOMAIN)) {
    const path = window.location.pathname;
    if (!path.includes('integrations') && !ANALYTICS_AVAILABLE_PAGES.some(page => path.includes(page))) {
      return true;
    }
  }
  return false;
};

const requireLogin = (meta: Meta): string => {
  const user = getLocal(localStorage).auth.getUser();
  const isLoggedIn = user?.user?.email;
  if (shouldMoveToIntegrations()) return '/integrations';
  if (origin.includes(INTEGRATIONS_DOMAIN)) return '';
  if (meta.auth) {
    if (!isLoggedIn) return '/analysis';
  } else if (meta.notAuth && isLoggedIn) return '/';
  return '';
};

const requireAdmin = (meta: Meta): string => {
  const isAdmin = getLocal(localStorage).auth.isAdmin();
  if (shouldMoveToIntegrations()) return '/integrations';
  if (origin.includes(INTEGRATIONS_DOMAIN)) return '';
  if (meta.admin) {
    if (!isAdmin) return '/analysis';
  } else if (meta.notAdmin && isAdmin) return '/admin/users-administration';
  return '';
};

const requireAnalytics = (meta: Meta): string => {
  const isAnalytics = getLocal(localStorage).auth.isAnalytics();
  if (meta.isAnalytics) {
    if (!origin.includes(INTEGRATIONS_DOMAIN)) return '/analysis';
    if (!isAnalytics) return '/integrations';
  }
  return '';
};

const requireLicenced = (meta: Meta): string => {
  if (shouldMoveToIntegrations()) return '/integrations';
  if (meta.licenced && !getLocal(localStorage).auth.hasLicence()) return '/account';
  return '';
}

const requirePayments = (meta: Meta): string => {
  if (shouldMoveToIntegrations()) return '/integrations';
  if (meta.payments) {
    const isPrepaid = getLocal(localStorage).auth.isPrepaid();
    const hasOnlyLicence = getLocal(localStorage).auth.hasOnlyLicence();
    if (isPrepaid || hasOnlyLicence) return '/analysis';
  }
  return '';
};

const guards = [requireLogin, requireAdmin, requireLicenced, requirePayments, requireAnalytics];

export type ProtectorProps = {
  name: string;
  elem: React.LazyExoticComponent<() => React.JSX.Element | null>;
  meta?: Meta;
}

const ProtectorProduction = ({ meta, elem, name }: ProtectorProps) => {
  const metaPro = meta ?? {};
  let res: string = '';
  for (let guard of guards) {
    res = guard(metaPro);
    if (res.length > 0) {
      break;
    }
  }
  if (res.length > 0) {
    if (res === '/integrations') {
      if (meta?.isAnalytics || !window.location.pathname.includes('integrations')) {
        window.location.replace('/integrations');
        return null;
      }
    }
    window.location.replace(res);
    return null;
  }
  const Comp = elem;
  return (
    <ErrorBoundary componentName={name}>
      <MainContextProviderProduction componentName={name} withOpenedSignIn={!!metaPro.withOpenedSignIn}>
        <Comp />
      </MainContextProviderProduction>
    </ErrorBoundary>
  );
};

export default ProtectorProduction;

export const Loader = () => (
  <MainContextProviderProduction componentName="loading">
    <FullscreenLoaderSVG text="Loading..." show={true}/>
  </MainContextProviderProduction>
);
